import { Close, DeleteOutlineOutlined, DeleteOutlineRounded, EditOutlined, Search, VisibilityOutlined, WarningAmberRounded } from '@mui/icons-material'
import { Avatar, Box, Button, Chip, Dialog, DialogContent, Fade, IconButton, InputAdornment, Pagination, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { ApiDeleteClient, ApiGetClientList } from '../../../Core/Apicall';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { Client } from '../../../types/Client';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { RootState } from '../../../Redux/store';
import { UserRole } from '../../../types/UserRole';

function Clients() {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const navigate = useNavigate()
    const [clientList, setClientList] = useState<Client[]>()
    const [total, setTotal] = useState<number>(-1)
    const [openDelete, setOpenDelete] = useState(false);
    const [selectedClient, setSelectedClient] = useState<Client | null>(null);
    const { userDetails } = useSelector((state: RootState) => state.global)
    const userRole: UserRole = (userDetails?.role as UserRole) ?? "client";
    const [search, setSearch] = useState('')


    const filteredClients = useMemo(() => {
        const term = search.trim().toLowerCase()
        if (!term) return clientList ?? []
        return (clientList ?? []).filter((c) =>
            [
                c.companyName,
                c.email,
                c.clientAdmin?.firstName,
                c.clientAdmin?.lastName,
                `${c.clientAdmin?.firstName ?? ''} ${c.clientAdmin?.lastName ?? ''}`,
            ].some((v) => v?.toLowerCase().includes(term))
        )
    }, [clientList, search])

    const getStatusColor = (status: string) => {
        switch (status) {
            case "Active":
                return {
                    color: "#16A34A",
                    bgcolor: "#DCFCE7",
                };
            case "Pending":
                return {
                    color: "#D97706",
                    bgcolor: "#FEF3C7",
                };
            case "Suspended":
                return {
                    color: "#DC2626",
                    bgcolor: "#FEE2E2",
                };
            default:
                return {};
        }
    };

    const getClientList = async () => {
        setClientList([])
        try {
            const data = `?offset=${page * rowsPerPage}&limit=${rowsPerPage}`
            const res = await ApiGetClientList(data)
            if (res?.status) {
                setClientList(res?.data)
                setTotal(res?.total)
            }

        } catch (error: any) {
            toast.error(error?.message || "Something went wrong")
        }
    }

    useEffect(() => {
        getClientList()
    }, [page])

    const handleClose = () => {
        setSelectedClient(null)
        setOpenDelete(false)
    }


    const onDelete = async () => {
        try {
            const data = `?clientId=${selectedClient?.clientId}`
            const res = await ApiDeleteClient(data)
            if (res?.status) {
                handleClose()
                getClientList()
            }
        } catch (error: any) {
            toast.error(error?.message || "Something went wrong")
        }
    }
    return (
        <Box className='p-lg-3 p-xl-4 p-2'>
            <Box className='mb-4 d-flex flex-column flex-md-row gap-3 align-items-start align-items-md-center justify-content-between'>
                <Box>
                    <Typography variant='h5' className='fw-bold' sx={{ lineHeight: 'normal' }}>All Clients</Typography>
                    <Typography variant='body2'>Manage all your platform clients</Typography>
                </Box>
                <Box className='d-flex align-items-center gap-2 text-field justify-content-md-end justify-content-between w-100'>
                    <TextField
                        className='m-0 p-0'
                        placeholder='Search Clients...'
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ minWidth: '300px' }}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start" className='me-0'>
                                        <Search fontSize="small" className='m-0 p-0' />
                                    </InputAdornment>
                                ),
                                endAdornment: search && (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setSearch('')}>
                                            <Close fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                    <Button className='btn-main px-3 py-2' onClick={() => { navigate(`/${userRole}/add-or-update-client`) }}>+ Add Client</Button>
                </Box>
            </Box>
            <Box className=''>
                <Box className='bg-white'>
                    <TableContainer className='w-100'
                        sx={{
                            border: "1px solid #E5E7EB",
                            borderRadius: "10px",
                            // overflow: "hidden",
                        }}
                    >
                        <Table>
                            <TableHead>
                                <TableRow
                                    sx={{
                                        bgcolor: "var(--primary-light-color)",
                                        "& th": {
                                            fontWeight: 600,
                                            color: "#555",
                                            py: 2,
                                            borderBottom: "1px solid #E5E7EB",
                                        },
                                    }}
                                >
                                    {[
                                        "Client",
                                        "Owner",
                                        "Email",
                                        // "PLAN",
                                        // "STATUS",
                                        "Dealers",
                                        "Products",
                                        "Joined At",
                                        "Actions",
                                    ].map((item) => (
                                        <TableCell key={item}>
                                            <Typography
                                                variant="caption"
                                                className={`${item === "Dealers" || item === "Products" ? "text-center" : ""} d-block`}
                                            >
                                                {item}
                                            </Typography>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredClients?.map((row: Client) => (
                                    <TableRow
                                        key={row.clientId}
                                        hover
                                        sx={{
                                            "& td": {
                                                py: 2,
                                            },
                                        }}
                                    >
                                        <TableCell>
                                            <Stack direction="row" spacing={2} className='align-items-center'>
                                                <Avatar
                                                    sx={{
                                                        width: 32,
                                                        height: 32,
                                                        bgcolor: "var(--primary-color)",
                                                        fontSize: 14,
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    {row.clientAdmin.firstName[0]}{row.clientAdmin.lastName[0]}
                                                </Avatar>

                                                <Typography>
                                                    {row.companyName}
                                                </Typography>
                                            </Stack>
                                        </TableCell>

                                        <TableCell>{row.companyName}</TableCell>

                                        <TableCell>{row.email}</TableCell>

                                        {/* <TableCell>
                                        <Chip
                                            size="small"
                                            label={10000}
                                            sx={{
                                                bgcolor: "#F5F5F5",
                                            }}
                                        />
                                    </TableCell> */}

                                        {/* <TableCell>
                                        <Chip
                                            size="small"
                                            label={row.sg}
                                            sx={{
                                                ...getStatusColor(row.status),
                                                fontWeight: 600,
                                            }}
                                        />
                                    </TableCell> */}

                                        <TableCell className='text-center'>{row?.totalDealers}</TableCell>

                                        <TableCell className='text-center'>{row?.totalProducts}</TableCell>

                                        <TableCell>{dayjs(row.createdAt).format("DD/MM/YYYY HH:MM")}</TableCell>

                                        <TableCell>
                                            <Stack direction="row" spacing={1}>
                                                <IconButton size="small" onClick={() => { navigate(`/${userRole}/add-or-update-client/${row?.clientId}`) }}>
                                                    <EditOutlined className='text-main' fontSize="small" />
                                                </IconButton>

                                                <IconButton size="small" onClick={() => { setSelectedClient(row); setOpenDelete(true) }}>
                                                    <DeleteOutlineOutlined className='text-red' fontSize="small" />
                                                </IconButton>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
                <Box className='p-3 d-flex justify-content-end'>
                    {total > rowsPerPage && <Pagination
                        page={page + 1}
                        count={Math.ceil(total / rowsPerPage)}
                        onChange={(_, value) => setPage(value - 1)}
                        shape="rounded"
                        // color="primary"
                        sx={{
                            "& .MuiPaginationItem-root.Mui-selected": {
                                backgroundColor: 'var(--primary-color)',
                                color: 'white'
                            }
                        }}
                        showFirstButton={false}
                        showLastButton={false}
                        siblingCount={1}
                        boundaryCount={1}
                    />}
                </Box>
            </Box>

            <Dialog
                open={openDelete}
                onClose={() => { handleClose() }}
                maxWidth="sm"
                fullWidth
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: "10px",
                            p: 1,
                            overflow: "hidden",
                        },
                    },
                }}
            >
                <DialogContent sx={{ p: 4, position: "relative" }}>
                    <IconButton
                        onClick={() => { handleClose() }}
                        sx={{
                            position: "absolute",
                            right: 18,
                            top: 18,
                        }}
                    >
                        <Close />
                    </IconButton>


                    <Box className='d-flex align-items-center  justify-content-between mb-3'>
                        <Box
                            className='mx-auto'
                            sx={{
                                width: 100,
                                height: 100,
                                borderRadius: "50%",
                                bgcolor: "#FDECEC",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                position: "relative",
                            }}
                        >
                            <DeleteOutlineRounded
                                className='text-red'
                                sx={{
                                    fontSize: 60,
                                }}
                            />

                            <WarningAmberRounded
                                className='text-red'
                                sx={{
                                    position: "absolute",
                                    bottom: 18,
                                    right: 14,
                                    bgcolor: "#fff",
                                    borderRadius: "50%",
                                }}
                            />
                        </Box>
                    </Box>

                    <Typography
                        variant="h5"
                        className='text-center fw-bold'
                        gutterBottom
                    >
                        Delete Client?
                    </Typography>

                    <Typography
                        className='text-grey text-center mb-4 mx-5'
                    >
                        Are you sure you want to delete this client?
                        This action cannot be undone.
                    </Typography>

                    <Box
                        className='p-3 border-main border-10 mb-3'>
                        <Stack
                            direction="row"
                            className='justify-content-between mb-2'
                        >
                            <Typography className='text-grey'>
                                Company Name
                            </Typography>

                            <Typography className='fw-bold' >
                                {selectedClient?.companyName}
                            </Typography>
                        </Stack>

                        <Stack
                            direction="row"
                            className='justify-content-between mb-2'
                        >
                            <Typography className='text-grey'>
                                Company Email
                            </Typography>

                            <Typography className='fw-bold'>
                                {selectedClient?.email}
                            </Typography>
                        </Stack>

                        <Stack
                            direction="row"
                            className='justify-content-between'
                        >
                            <Typography className='text-grey'>
                                Created On
                            </Typography>

                            <Typography className='fw-bold'>
                                {dayjs(selectedClient?.createdAt).format("DD MMM YYYY")}
                            </Typography>
                        </Stack>
                    </Box>

                    {/* Warning */}

                    <Box
                        className='text-red'
                        sx={{
                            bgcolor: "#FFF3F3",
                            border: "1px solid var(--danger-color)",
                            borderRadius: 3,
                            p: 2,
                            display: "flex",
                            gap: 2,
                            mb: 4,
                        }}
                    >
                        <WarningAmberRounded />

                        <Typography variant="body2">
                            All client data including users,
                            products, orders and settings
                            will be permanently deleted.
                        </Typography>
                    </Box>

                    {/* Buttons */}

                    <Stack direction="row" spacing={2}>
                        <Button
                            className='btn-outlined-main'
                            fullWidth
                            variant="outlined"
                            onClick={() => { handleClose() }}
                            sx={{
                                height: 52,
                                borderRadius: 2,
                                textTransform: "none",
                            }}
                        >
                            Cancel
                        </Button>

                        <Button
                            fullWidth
                            variant="contained"
                            startIcon={<DeleteOutlineRounded />}
                            className='border-10'
                            onClick={onDelete}
                            sx={{
                                bgcolor: "var(--danger-color)",
                                textTransform: "none",
                                fontWeight: 600,
                            }}
                        >
                            Delete Client
                        </Button>
                    </Stack>
                </DialogContent>
            </Dialog>
        </Box>
    )
}

export default Clients
